
import React from "react";

import {
  FaCoffee,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGlobeEurope,
  FaHeart,
  FaLock,
  FaPhoneAlt,
  FaRocket,
} from "react-icons/fa";

import VKardLink from "./VKardLink";

import {
  DEVELOPER_PROFILE,
  DONATION_LINKS,
} from "../utils/productsUtils";

/* ========================================================================== */
/*                           CONFIGURATION SOUTIEN                            */
/* ========================================================================== */

const SUPPORT_CONFIG = {
  2: {
    Icon: FaCoffee,
    title: "Un petit café",
    description: "Un geste qui fait plaisir",
  },
  5: {
    Icon: FaHeart,
    title: "Un coup de pouce",
    description: "Un soutien précieux",
  },
  10: {
    Icon: FaRocket,
    title: "Un grand soutien",
    description: "Un bel encouragement",
  },
};

/* ========================================================================== */
/*                             CARTE DE SOUTIEN                               */
/* ========================================================================== */

function DeveloperDonationSection() {
  if (!DONATION_LINKS?.length) return null;

  return (
    <section
      id="soutenir-developpeur"
      aria-labelledby="developer-support-title"
      className="border-t border-slate-200 bg-gradient-to-br from-rose-50/70 via-white to-blue-50/70 px-5 py-6 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/70 sm:px-7 sm:py-8"
    >
      <div className="mx-auto max-w-4xl">

        {/* Introduction */}
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400">
            <FaHeart size={22} />
          </div>

          <p className="mt-4 text-xs font-extrabold uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400">
            Un petit geste, un grand encouragement
          </p>

          <h3
            id="developer-support-title"
            className="mt-2 text-xl font-black tracking-tight text-slate-950 dark:text-white sm:text-2xl"
          >
            Vous aimez votre application ?
          </h3>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-600 dark:text-slate-300">
            Derrière ces applications, il y a des heures de
            développement, de recherche et d'améliorations.

            {" "}Si mon travail vous est utile, vous pouvez
            m'encourager en m'offrant symboliquement un café
            ou en apportant un petit soutien.

            {" "}Chaque geste me motive à continuer de créer
            des outils pour les clubs sportifs.
          </p>
        </div>

        {/* Les trois montants */}
        <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {DONATION_LINKS.map((donation) => {
            const config =
              SUPPORT_CONFIG[donation.amount] || {
                Icon: FaHeart,
                title: donation.title || "Soutenir",
                description: "Merci pour votre soutien",
              };

            const Icon = config.Icon;

            return (
              <a
                key={donation.amount}
                href={donation.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${config.title} : ${donation.label}`}
                className="group relative flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-rose-300 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:hover:border-rose-500"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500 transition-transform group-hover:scale-110 dark:bg-rose-900/20 dark:text-rose-400">
                  <Icon size={22} />
                </div>

                <span className="mt-3 text-3xl font-black tracking-tight text-slate-950 dark:text-white">
                  {donation.label}
                </span>

                <span className="mt-2 text-sm font-extrabold text-slate-800 dark:text-slate-100">
                  {config.title}
                </span>

                <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {config.description}
                </span>

                <span className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#013E8E] px-4 py-2 text-xs font-bold text-white transition-colors group-hover:bg-blue-800">
                  Soutenir
                  <FaExternalLinkAlt size={10} />
                </span>
              </a>
            );
          })}
        </div>

        {/* Informations */}
        <div className="mt-6 flex flex-col items-center gap-2 text-center">
          <p className="flex flex-wrap items-center justify-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            <FaLock className="text-emerald-600" />
            Paiement unique et sécurisé par Stripe
          </p>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Participation libre, entièrement facultative
            et sans engagement.
          </p>

          <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            Merci pour votre confiance et vos encouragements !
          </p>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================== */
/*                           PRÉSENTATION DÉVELOPPEUR                          */
/* ========================================================================== */

export default function DeveloperHeroCard() {
  return (
    <article className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">

      {/* ================================================================ */}
      {/* HEADER                                                           */}
      {/* ================================================================ */}

      <div className="relative overflow-hidden bg-slate-950 px-5 py-7 text-white sm:px-7 sm:py-8">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(76,168,47,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(1,62,142,0.4),transparent_38%)]" />

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/60">
              À propos du développeur
            </p>

            <h2 className="mt-2 text-2xl font-black tracking-tight sm:text-3xl">
              Des applications pensées pour les clubs
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-white/80">
              Des outils web conçus avec passion pour simplifier
              le quotidien des clubs, associations, enseignants,
              dirigeants et adhérents.
            </p>
          </div>

          {DONATION_LINKS?.length > 0 && (
            <a
              href="#soutenir-developpeur"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100"
            >
              <FaHeart className="text-rose-500" />
              Soutenir mes créations
            </a>
          )}
        </div>
      </div>

      {/* ================================================================ */}
      {/* PROFIL DU DÉVELOPPEUR                                            */}
      {/* ================================================================ */}

      <div className="p-5 sm:p-7">
        <div className="flex flex-col gap-6 md:flex-row md:items-center">

          {/* Photo */}
          <div className="relative mx-auto shrink-0 md:mx-0">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-[#013E8E] via-indigo-500 to-[#4CA82F] opacity-80 blur-sm" />

            <img
              src={DEVELOPER_PROFILE.photo}
              alt={DEVELOPER_PROFILE.name}
              className="relative h-28 w-28 rounded-full object-cover ring-4 ring-white dark:ring-slate-900 sm:h-32 sm:w-32"
            />
          </div>

          {/* Présentation */}
          <div className="min-w-0 flex-1 text-center md:text-left">

            <h3 className="text-xl font-black text-slate-950 dark:text-white">
              {DEVELOPER_PROFILE.name}
            </h3>

            <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
              {DEVELOPER_PROFILE.role}
            </p>

            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
              Passionné de développement web et de judo,
              je crée des applications pour faciliter
              la gestion des clubs sportifs et associations.

              {" "}Mon objectif est simple : proposer
              des outils pratiques, accessibles et adaptés
              aux besoins réels des dirigeants, enseignants
              et adhérents.

              {" "}Vos retours et vos encouragements
              m'aident chaque jour à faire évoluer ces projets.
            </p>

            {/* Coordonnées */}
            <div className="mt-5 flex flex-wrap justify-center gap-2 md:justify-start">

              <a
                href={`mailto:${DEVELOPER_PROFILE.email}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
              >
                <FaEnvelope />
                Email
              </a>

              <a
                href={`tel:${DEVELOPER_PROFILE.phoneHref}`}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
              >
                <FaPhoneAlt />
                {DEVELOPER_PROFILE.phoneDisplay}
              </a>

              <a
                href={DEVELOPER_PROFILE.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-blue-800"
              >
                <FaGlobeEurope />
                MonAppliClub
                <FaExternalLinkAlt className="text-xs opacity-80" />
              </a>

              <VKardLink />
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SOUTIEN AU DÉVELOPPEMENT                                         */}
      {/* ================================================================ */}

      <DeveloperDonationSection />
    </article>
  );
}
